<template>
    <div class="ai-float-wrap" :class="{ 'is-open': open }">
        <!-- 收起态：右侧贴边小机器人 -->
        <div v-if="!open" class="ai-dock" @click="handleOpen">
            <div class="dock-robot">
                <div class="robot-antenna">
                    <div class="antenna-ball"></div>
                </div>
                <div class="robot-head">
                    <div class="robot-eye left"></div>
                    <div class="robot-eye right"></div>
                    <div class="robot-mouth"></div>
                </div>
                <div class="robot-body">
                    <div class="robot-arm left"></div>
                    <div class="robot-arm right"></div>
                </div>
            </div>
            <div class="dock-label">智能助手</div>
        </div>

        <!-- 展开态：PC微信三栏 -->
        <div v-else class="wechat-panel">
            <!-- 顶部标题栏 -->
            <div class="wechat-header">
                <div class="header-left">
                    <RobotOutlined class="app-icon" />
                    <span class="app-title">OPC智能助手 · 加荣</span>
                </div>
                <div class="header-actions">
                    <PlusOutlined class="action-icon" title="新建对话" @click="handleNewConversation" />
                    <ClearOutlined class="action-icon" title="清空当前会话" @click="clearMessages" />
                    <CloseOutlined class="action-icon" title="收起" @click="open = false" />
                </div>
            </div>

            <!-- 主体三栏 -->
            <div class="wechat-body">
                <!-- ========== 左栏：会话列表 ========== -->
                <div class="conv-sidebar">
                    <div class="conv-search">
                        <a-input v-model:value="convKeyword" placeholder="搜索会话" size="small" allow-clear>
                            <template #prefix>
                                <SearchOutlined />
                            </template>
                        </a-input>
                    </div>
                    <div class="conv-list">
                        <a-dropdown v-for="conv in filteredConversations" :key="conv.id" trigger="contextmenu">
                            <div :class="['conv-item', { active: conv.id === currentConvId }]"
                                @click="switchConversation(conv.id)">
                                <div class="conv-avatar">
                                    <MessageOutlined />
                                </div>
                                <div class="conv-info">
                                    <div class="conv-title">{{ conv.title || '新对话' }}</div>
                                    <div class="conv-preview">{{ getConvPreview(conv) }}</div>
                                </div>
                                <div class="conv-time">{{ shortTime(conv.updatedAt) }}</div>
                            </div>
                            <template #overlay>
                                <a-menu>
                                    <a-menu-item key="delete" @click="deleteConversation(conv.id)">
                                        <DeleteOutlined /> 删除会话
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                        <a-empty v-if="!filteredConversations.length" description="暂无会话"
                            :image="Empty.PRESENTED_IMAGE_SIMPLE" />
                    </div>
                </div>

                <!-- ========== 中栏：聊天主区 ========== -->
                <div class="chat-main">
                    <!-- Tab 切换 -->
                    <div class="chat-tabs">
                        <div v-for="tab in tabs" :key="tab.key" :class="['tab-item', { active: activeTab === tab.key }]"
                            @click="switchTab(tab.key)">
                            <component :is="tab.icon" />
                            <span>{{ tab.label }}</span>
                        </div>
                    </div>

                    <!-- 对话 Tab -->
                    <template v-if="activeTab === 'chat'">
                        <div class="chat-messages" ref="msgRef">
                            <!-- 欢迎 -->
                            <div v-if="messages.length === 0" class="chat-welcome">
                                <div class="welcome-avatar">
                                    <RobotOutlined />
                                </div>
                                <p class="welcome-title">你好，我是 OPC 智能助手-加荣</p>
                                <p class="welcome-sub">查政策、写文案、看合同、找订单、拆任务</p>
                            </div>

                            <!-- 消息 -->
                            <div v-for="(m, i) in messages" :key="`${i}-${m.content.length}`"
                                :class="['msg-row', m.role === 'user' ? 'is-me' : 'is-other']">
                                <div class="msg-avatar">
                                    <RobotOutlined v-if="m.role === 'assistant'" />
                                    <UserOutlined v-else />
                                </div>
                                <div class="msg-body">
                                    <div class="msg-bubble">
                                        <div v-if="m.files && m.files.length" class="msg-files">
                                            <div v-for="(f, fi) in m.files" :key="fi" class="file-item">
                                                <PaperClipOutlined />
                                                <span>{{ f.name }}</span>
                                                <span class="file-size">({{ formatSize(f.size) }})</span>
                                            </div>
                                        </div>
                                        <div v-if="m.images && m.images.length" class="msg-images">
                                            <img v-for="(img, ii) in m.images" :key="ii" :src="img" />
                                        </div>
                                        <div class="msg-content" v-html="renderContent(m.content)"></div>
                                    </div>
                                    <div v-if="m.role === 'assistant' && m.content" class="msg-actions">
                                        <CopyOutlined class="action-icon" @click="copyText(m.content)" />
                                    </div>
                                </div>
                            </div>

                            <!-- 加载中 -->
                            <div v-if="loading && !streamingActive" class="msg-row is-other">
                                <div class="msg-avatar">
                                    <RobotOutlined />
                                </div>
                                <div class="msg-body">
                                    <div class="msg-bubble thinking">
                                        <a-spin size="small" /> 加荣正在思考...
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 快捷操作 -->
                        <div class="quick-actions">
                            <a-tag v-for="q in quickActions" :key="q" @click="sendQuick(q)">{{ q }}</a-tag>
                        </div>

                        <!-- 待发送文件 -->
                        <div v-if="pendingFiles.length" class="pending-files">
                            <div v-for="(f, i) in pendingFiles" :key="i" class="pending-item">
                                <img v-if="f.type.startsWith('image/')" :src="f.url" />
                                <template v-else>
                                    <FileOutlined />
                                    <span>{{ f.name }}</span>
                                </template>
                                <CloseCircleOutlined class="remove" @click="removePending(i)" />
                            </div>
                        </div>

                        <!-- 输入区：上输入框，下工具栏 -->
                        <div class="chat-input">
                            <a-textarea v-model:value="input" class="chat-textarea"
                                placeholder="输入消息... (Enter 发送，Shift+Enter 换行)" :auto-size="{ minRows: 3, maxRows: 8 }"
                                @pressEnter="handleEnter" />

                            <div class="input-bottom-bar">
                                <div class="input-tools">
                                    <PictureOutlined class="action-icon" title="上传图片" @click="triggerUpload('image')" />
                                    <PaperClipOutlined class="action-icon" title="上传文件"
                                        @click="triggerUpload('file')" />
                                    <input ref="imageInput" type="file" accept="image/*" multiple hidden
                                        @change="onFileChange" />
                                    <input ref="fileInput" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.csv"
                                        multiple hidden @change="onFileChange" />
                                </div>
                                <a-button type="primary" class="send-btn" :loading="loading" @click="send">
                                    发送
                                </a-button>
                            </div>
                        </div>
                    </template>

                    <!-- 数字员工 Tab -->
                    <template v-else-if="activeTab === 'agents'">
                        <div class="ai-agents">
                            <div class="agents-header">
                                <span>社区数字员工</span>
                                <a-button type="link" size="small" @click="createAgentDialog = true">+ 定制</a-button>
                            </div>
                            <div class="agents-grid">
                                <div v-for="agent in digitalAgents" :key="agent.id" class="agent-card"
                                    :class="{ active: activeAgentId === agent.id }" @click="selectAgent(agent)">
                                    <div class="agent-avatar">{{ agent.emoji }}</div>
                                    <div class="agent-info">
                                        <div class="agent-name">{{ agent.name }}</div>
                                        <div class="agent-role">{{ agent.role }}</div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="activeAgentId" class="agent-chat-hint">
                                已选择 <strong>{{ activeAgent?.name }}</strong>，切换到对话页开始咨询
                            </div>
                        </div>
                    </template>
                </div>

                <!-- ========== 右栏：知识库 ========== -->
                <div class="knowledge-sidebar">
                    <div class="knowledge-header">
                        <DatabaseOutlined class="kb-icon" />
                        <span>知识库</span>
                        <a-badge :count="selectedKnowledge.length" :number-style="{ backgroundColor: '#1677ff' }" />
                    </div>

                    <div class="knowledge-search">
                        <a-input v-model:value="kbKeyword" placeholder="搜索知识" size="small" allow-clear>
                            <template #prefix>
                                <SearchOutlined />
                            </template>
                        </a-input>
                    </div>

                    <!-- 分类 tab -->
                    <div class="kb-tabs">
                        <span v-for="t in kbTabs" :key="t" :class="['kb-tab', { active: kbActiveTag === t }]"
                            @click="kbActiveTag = t">{{ t }}</span>
                    </div>

                    <div class="knowledge-list">
                        <div v-for="kb in filteredKnowledge" :key="kb.id" class="knowledge-card"
                            :class="{ active: selectedKnowledge.includes(kb.id) }" @click="handleKnowledgeClick(kb)">
                            <div class="kb-card-head">
                                <component :is="kb.icon" class="kb-card-icon" />
                                <span class="kb-card-title" v-html="highlightText(kb.title)"></span>
                                <CheckCircleFilled v-if="selectedKnowledge.includes(kb.id)" class="kb-checked" />
                            </div>
                        </div>
                        <a-empty v-if="!filteredKnowledge.length" description="没有匹配的知识"
                            :image="Empty.PRESENTED_IMAGE_SIMPLE" />
                    </div>

                    <div class="knowledge-tip">
                        <RobotOutlined />
                        <span v-if="selectedKnowledge.length">
                            已选 {{ selectedKnowledge.length }} 条作为上下文
                        </span>
                        <span v-else>点击知识卡片，自动填入问题</span>
                    </div>
                </div>
            </div>

            <!-- 定制数字员工弹窗 -->
            <a-modal v-model:open="createAgentDialog" title="定制数字员工" :width="480" @ok="submitAgentCustom"
                :confirm-loading="agentSubmitting">
                <div style="margin-bottom: 12px; color: #666; font-size: 13px;">
                    描述你希望这个数字员工做什么，管理员会尽快为你配置。
                </div>
                <a-textarea v-model:value="agentCustomText" :rows="4"
                    placeholder="例如：我想要一个专门帮我处理小红书笔记文案的助手，风格活泼一点..." />
            </a-modal>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, watch } from 'vue'
import {
    RobotOutlined, CloseOutlined, ClearOutlined, CopyOutlined,
    PaperClipOutlined, PictureOutlined, FileOutlined, CloseCircleOutlined,
    MessageOutlined, TeamOutlined, PlusOutlined,
    SearchOutlined, DeleteOutlined, UserOutlined,
    DatabaseOutlined, CheckCircleFilled,
    FileTextOutlined, FileProtectOutlined,
    EnvironmentOutlined, ApartmentOutlined, QuestionCircleOutlined
} from '@ant-design/icons-vue'
import { message, Empty } from 'ant-design-vue'
import baseService from '@/utils/http/axios'
import ls from '@/utils/Storage'

interface MsgFile { name: string; size: number; type: string }
interface Msg { role: 'user' | 'assistant'; content: string; files?: MsgFile[]; images?: string[] }
interface DigitalAgent { id: number; emoji: string; name: string; role: string; description?: string }
interface Conversation {
    id: string
    title: string
    messages: Msg[]
    updatedAt: string
}
interface KnowledgeItem {
    id: string
    title: string
    tag: string
    tagColor: string
    icon: any
}

const CONV_LIST_KEY = 'OPC_AI_CONV_LIST'
const CURRENT_CONV_KEY = 'OPC_AI_CURRENT_CONV'

// ========== 主面板 ==========
const open = ref(false)
const activeTab = ref<'chat' | 'agents'>('chat')

const tabs = [
    { key: 'chat' as const, label: '对话', icon: MessageOutlined },
    { key: 'agents' as const, label: '数字员工', icon: TeamOutlined },
]

const switchTab = (key: 'chat' | 'agents') => {
    activeTab.value = key
    if (key === 'agents' && !digitalAgents.value.length) loadAgents()
}

const handleOpen = () => {
    if (!currentConv.value || currentConv.value.messages.length > 0) {
        newConversation()
    }
    open.value = true
    nextTick(scrollToBottom)
}

const handleNewConversation = () => {
    newConversation()
    nextTick(scrollToBottom)
}

// ========== 会话管理 ==========
const conversations = ref<Conversation[]>(JSON.parse(ls.get(CONV_LIST_KEY) || '[]'))
const currentConvId = ref<string>(ls.get(CURRENT_CONV_KEY) || '')
const convKeyword = ref('')

const currentConv = computed(() => conversations.value.find(c => c.id === currentConvId.value))

const filteredConversations = computed(() => {
    if (!convKeyword.value.trim()) return conversations.value
    const kw = convKeyword.value.trim().toLowerCase()
    return conversations.value.filter(c =>
        (c.title || '').toLowerCase().includes(kw) ||
        c.messages.some(m => m.content.toLowerCase().includes(kw))
    )
})

const getConvPreview = (conv: Conversation) => {
    const last = conv.messages[conv.messages.length - 1]
    if (!last) return '暂无消息'
    const text = last.content.replace(/\n/g, ' ').slice(0, 20)
    return last.role === 'user' ? `我: ${text}` : text
}

const shortTime = (timeStr: string) => {
    if (!timeStr) return ''
    try {
        const d = new Date(timeStr.replace(/\//g, '-'))
        const now = new Date()
        const diff = now.getTime() - d.getTime()
        if (diff < 60 * 1000) return '刚刚'
        if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}分钟前`
        if (d.toDateString() === now.toDateString()) {
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        }
        return `${d.getMonth() + 1}/${d.getDate()}`
    } catch {
        return ''
    }
}

const ensureConversation = () => {
    if (!conversations.value.length) {
        newConversation()
    } else if (!currentConv.value) {
        currentConvId.value = conversations.value[0].id
        ls.set(CURRENT_CONV_KEY, currentConvId.value)
    }
}

const newConversation = () => {
    const id = 'c_' + Date.now()
    const conv: Conversation = {
        id,
        title: '新对话',
        messages: [],
        updatedAt: new Date().toLocaleString('zh-CN', { hour12: false })
    }
    conversations.value.unshift(conv)
    currentConvId.value = id
    saveConversations()
    ls.set(CURRENT_CONV_KEY, id)
}

const switchConversation = (id: string) => {
    currentConvId.value = id
    ls.set(CURRENT_CONV_KEY, id)
    scrollToBottom()
}

const deleteConversation = (id: string) => {
    const idx = conversations.value.findIndex(c => c.id === id)
    if (idx === -1) return
    conversations.value.splice(idx, 1)
    if (currentConvId.value === id) {
        if (conversations.value.length) {
            currentConvId.value = conversations.value[0].id
            ls.set(CURRENT_CONV_KEY, currentConvId.value)
        } else {
            newConversation()
        }
    }
    saveConversations()
    message.success('已删除')
}

const saveConversations = () => {
    ls.set(CONV_LIST_KEY, JSON.stringify(conversations.value.slice(0, 50)))
}

const messages = computed({
    get() {
        return currentConv.value?.messages || []
    },
    set(val: Msg[]) {
        if (currentConv.value) {
            currentConv.value.messages = val
            currentConv.value.updatedAt = new Date().toLocaleString('zh-CN', { hour12: false })
            if ((!currentConv.value.title || currentConv.value.title === '新对话') && val.length) {
                const firstUser = val.find(m => m.role === 'user')
                if (firstUser) {
                    currentConv.value.title = firstUser.content.slice(0, 20) || '新对话'
                }
            }
            saveConversations()
        }
    }
})

// ========== 对话逻辑 ==========
const input = ref('')
const loading = ref(false)
const streamingActive = ref(false)
const msgRef = ref<HTMLElement>()
const imageInput = ref<HTMLInputElement>()
const fileInput = ref<HTMLInputElement>()
const pendingFiles = ref<(MsgFile & { url?: string; file: File })[]>([])

const quickActions = ['客户咨询怎么回', '入驻方案怎么写', '客户嫌贵怎么谈', '怎么找第一批客户']

const sendQuick = (q: string) => { activeTab.value = 'chat'; input.value = q; send() }

const handleEnter = (e: KeyboardEvent) => {
    if (e.shiftKey) return
    e.preventDefault()
    send()
}

const triggerUpload = (type: 'image' | 'file') => {
    type === 'image' ? imageInput.value?.click() : fileInput.value?.click()
}

const onFileChange = (e: Event) => {
    const files = (e.target as HTMLInputElement).files
    if (!files) return
    Array.from(files).forEach((file) => {
        if (file.size > 20 * 1024 * 1024) { message.warning(`${file.name} 超过 20MB`); return }
        const item: any = { name: file.name, size: file.size, type: file.type, file }
        if (file.type.startsWith('image/')) item.url = URL.createObjectURL(file)
        pendingFiles.value.push(item)
    })
        ; (e.target as HTMLInputElement).value = ''
}

const removePending = (i: number) => {
    if (pendingFiles.value[i].url) URL.revokeObjectURL(pendingFiles.value[i].url!)
    pendingFiles.value.splice(i, 1)
}

const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + 'B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
    return (bytes / 1024 / 1024).toFixed(1) + 'MB'
}

const renderContent = (text: string) => {
    let html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // 代码块
        .replace(/```([\s\S]*?)```/g, '<pre class="code-block">$1</pre>')
        // 标题
        .replace(/^### (.*$)/gm, '<h4>$1</h4>')
        .replace(/^## (.*$)/gm, '<h3>$1</h3>')
        .replace(/^# (.*$)/gm, '<h3>$1</h3>')
        // 加粗
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // 行内代码
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        // 无序列表
        .replace(/^\s*-\s+(.*$)/gm, '<li>$1</li>')
        // 换行
        .replace(/\n/g, '<br/>')
    return html
}

const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text)
    message.success('已复制')
}

const clearMessages = () => {
    if (currentConv.value) {
        currentConv.value.messages = []
        currentConv.value.title = '新对话'
        saveConversations()
    }
    message.success('当前会话已清空')
}

const scrollToBottom = async () => {
    await nextTick()
    const el = msgRef.value
    if (!el) return
    // 如果用户手动上滑了，就别强制拉底
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    if (isNearBottom) {
        el.scrollTop = el.scrollHeight
    }
}

const getToken = () => {
    const t = ls.get('ACCESS_TOKEN') || ''
    return t.startsWith('Bearer') ? t : `Bearer ${t}`
}

const isFailedReply = (text: string) => {
    if (!text) return false
    return text === '请求失败，请稍后重试。' ||
        text.includes('AI 服务暂时不可用') ||
        text.includes('[错误：')
}

const send = async () => {
    if ((!input.value.trim() && !pendingFiles.value.length) || loading.value) return
    if (!currentConv.value) return

    const userMsg: Msg = {
        role: 'user',
        content: input.value,
        files: pendingFiles.value.map(f => ({ name: f.name, size: f.size, type: f.type })),
        images: pendingFiles.value.filter(f => f.url).map(f => f.url!)
    }
    const msgs = [...currentConv.value.messages, userMsg]
    const filesToSend = pendingFiles.value.map(f => f.file)
    input.value = ''
    pendingFiles.value = []
    loading.value = true
    streamingActive.value = true
    messages.value = msgs
    scrollToBottom()

    const assistantMsg: Msg = { role: 'assistant', content: '' }
    messages.value = [...msgs, assistantMsg]
    const assistantIndex = messages.value.length - 1

    const isAgent = activeAgentId.value !== null
    const recentMsgs = msgs.slice(-20)

    const formData = new FormData()
    formData.append('messages', JSON.stringify(recentMsgs.map(m => ({ role: m.role, content: m.content }))))
    filesToSend.forEach(f => formData.append('files[]', f))
    if (isAgent) formData.append('agent_id', String(activeAgentId.value))
    if (selectedKnowledge.value.length) {
        formData.append('knowledge_ids', JSON.stringify(selectedKnowledge.value))
    }

    const removeIfFailed = () => {
        const last = messages.value[assistantIndex]
        if (last && last.role === 'assistant' && isFailedReply(last.content)) {
            messages.value = messages.value.filter((_, i) => i !== assistantIndex)
        }
    }

    if (isAgent) {
        baseService({
            url: '/admin/v1/ai/agents/chat',
            method: 'post',
            data: formData
        })
            .then((res: any) => {
                messages.value[assistantIndex].content = res.data?.content || '暂无回复'
                messages.value = [...messages.value]
            })
            .catch((res: any) => {
                messages.value[assistantIndex].content = res.msg || '请求失败，请稍后重试。'
                messages.value = [...messages.value]
            })
            .finally(() => {
                removeIfFailed()
                loading.value = false
                streamingActive.value = false
                scrollToBottom()
            })
        return
    }

    try {
        const res = await fetch('/hncskaiyela/admin/v1/ai/chat/stream', {
            method: 'POST',
            headers: { Authorization: getToken() },
            body: formData
        })

        if (!res.ok || !res.body) {
            messages.value[assistantIndex].content = '请求失败，请稍后重试。'
            messages.value = [...messages.value]
            return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })

            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
                const trimmed = line.trim()
                if (!trimmed.startsWith('data: ')) continue
                const data = trimmed.slice(6)
                if (data === '[DONE]') continue
                try {
                    const json = JSON.parse(data)
                    if (json.content) {
                        messages.value[assistantIndex].content += json.content
                        scrollToBottom()
                    }
                    if (json.error) {
                        messages.value[assistantIndex].content += `\n[错误：${json.error}]`
                        messages.value = [...messages.value]
                    }
                } catch { /* ignore */ }
            }
        }
    } catch (e) {
        messages.value[assistantIndex].content = '请求失败，请稍后重试。'
        messages.value = [...messages.value]
    } finally {
        removeIfFailed()
        loading.value = false
        streamingActive.value = false
        scrollToBottom()
    }
}

// ========== 数字员工 ==========
const activeAgentId = ref<number | null>(null)
const createAgentDialog = ref(false)
const agentSubmitting = ref(false)
const agentCustomText = ref('')
const digitalAgents = ref<DigitalAgent[]>([])

const loadAgents = () => {
    baseService({
        url: '/admin/v1/ai/agents/list',
        method: 'post',
        data: {}
    })
        .then((res: any) => {
            digitalAgents.value = res.data || []
        })
        .catch((res: any) => {
            message.error(res.msg || '获取数字员工失败')
        })
}

const activeAgent = computed(() => digitalAgents.value.find(a => a.id === activeAgentId.value))

const selectAgent = (agent: DigitalAgent) => {
    activeAgentId.value = agent.id
    message.success(`已切换至 ${agent.name} 模式`)
}

const submitAgentCustom = () => {
    if (!agentCustomText.value.trim()) {
        message.warning('请描述你想要的数字员工')
        return
    }
    agentSubmitting.value = true
    baseService({
        url: '/admin/v1/ai/agents/custom-request',
        method: 'post',
        data: { description: agentCustomText.value.trim() }
    })
        .then(() => {
            message.success('已提交，管理员会尽快处理')
            createAgentDialog.value = false
            agentCustomText.value = ''
        })
        .catch((res: any) => {
            message.error(res.msg || '提交失败')
        })
        .finally(() => {
            agentSubmitting.value = false
        })
}

// ========== 知识库 ==========
const kbKeyword = ref('')
const kbActiveTag = ref('全部')
const kbTabs = ['全部', '工商', '税务']
const selectedKnowledge = ref<string[]>([])

const knowledgeList = ref<KnowledgeItem[]>([
    // ================= 工商版块（120条） =================
    // —— 公司注册 ——
    { id: 'gs_001', title: '注册公司需要什么资料', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_002', title: '注册公司流程是什么', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_003', title: '注册公司要多久', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_005', title: '公司核名怎么操作', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_006', title: '核名总是驳回怎么办', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_007', title: '公司名称格式怎么写', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_008', title: '经营范围怎么写', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_009', title: '经营范围可以随便写吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_010', title: '注册资本填多少合适', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_011', title: '注册资本要实缴吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_012', title: '认缴和实缴有什么区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_013', title: '注册资本写大了有什么风险', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_014', title: '注册公司要法人到场吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_015', title: '法人可以不是股东吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_016', title: '监事是干什么的', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_017', title: '股东和法人有什么区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_018', title: '一个人可以注册公司吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_019', title: '公司章程是什么', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_020', title: '营业执照多久能下来', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_021', title: '电子营业执照怎么领', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_022', title: '营业执照丢了怎么补', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_023', title: '公司注册后要做什么', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_024', title: '没有地址可以注册公司吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_025', title: '住宅可以作为注册地址吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },

    // —— 公司类型选择 ——
    { id: 'gs_026', title: '有限公司和个体户区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_027', title: '有限公司和个独区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_028', title: '个体户和个独哪个好', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_029', title: '有限责任公司和股份有限公司区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_030', title: '什么是合伙企业', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_031', title: '什么是个人独资企业', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_032', title: '什么是一人有限公司', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_033', title: '什么是分公司', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_034', title: '什么是子公司', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_035', title: '分公司和子公司区别', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_036', title: '个体户需要记账吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_037', title: '个体户可以开对公户吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_038', title: '个体户可以转让吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_039', title: '个体户需要年检吗', tag: '工商', tagColor: 'green', icon: FileTextOutlined },
    { id: 'gs_040', title: '个独可以几个股东', tag: '工商', tagColor: 'green', icon: FileTextOutlined },

    // —— 个独 / 个体户注册 ——
    { id: 'gs_041', title: '注册个独需要什么资料', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_042', title: '个独注册流程是什么', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_043', title: '个独注册需要多久', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_045', title: '个独需要法人到场吗', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_046', title: '个独能开分公司吗', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_047', title: '个独可以变更法人吗', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_048', title: '注册个体户需要什么资料', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_049', title: '个体户注册流程是什么', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_050', title: '个体户注册需要多久', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },
    { id: 'gs_052', title: '个体户能变更经营者吗', tag: '工商', tagColor: 'blue', icon: FileProtectOutlined },

    // —— 地址挂靠（核心） ——
    { id: 'gs_053', title: '地址挂靠是什么意思', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_054', title: '什么是虚拟地址注册', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_055', title: '挂靠地址合法吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_056', title: '挂靠地址一年多少钱', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_057', title: '挂靠地址怎么收费', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_058', title: '挂靠地址可以长期用吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_059', title: '挂靠地址会被查吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_060', title: '地址异常怎么解除', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_061', title: '为什么会被列入经营异常', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_062', title: '地址异常会影响法人吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_063', title: '挂靠地址能收信件吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_064', title: '工商局会实地核查吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_065', title: '挂靠地址需要提供什么资料', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_066', title: '一个地址能注册几家公司', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_067', title: '地址挂靠和地址托管区别', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_068', title: '什么是红本地址', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_069', title: '红本地址和普通挂靠区别', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_070', title: '地址挂靠需要租赁合同吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_071', title: '地址变更需要什么资料', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_072', title: '跨区变更地址怎么操作', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_073', title: '地址变更需要多久', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_074', title: '地址挂靠到期了怎么办', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_075', title: '挂靠地址能办许可证吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_076', title: '哪些行业不能用挂靠地址', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_077', title: '电商可以用挂靠地址吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },
    { id: 'gs_078', title: '地址挂靠可以开发票吗', tag: '工商', tagColor: 'orange', icon: EnvironmentOutlined },

    // —— 孵化器 / 园区 ——
    { id: 'gs_079', title: '孵化器地址能注册公司吗', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_080', title: '什么是创业孵化器', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_081', title: '入驻孵化器需要什么条件', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_082', title: '孵化器有什么政策补贴', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_083', title: '孵化器和众创空间区别', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_084', title: '园区地址和孵化器地址区别', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_085', title: '入驻孵化器要交钱吗', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_086', title: '园区返税是怎么回事', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_087', title: '什么是总部经济园区', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_088', title: '园区注册和普通注册区别', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_089', title: '园区地址可以异地经营吗', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },
    { id: 'gs_090', title: '入驻孵化器有什么好处', tag: '工商', tagColor: 'purple', icon: ApartmentOutlined },

    // —— 变更 ——
    { id: 'gs_091', title: '公司变更怎么做', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_092', title: '变更法人需要什么资料', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_093', title: '变更股东需要什么资料', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_094', title: '变更经营范围怎么操作', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_095', title: '变更公司名称怎么操作', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_096', title: '变更注册资本怎么操作', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_097', title: '增资和减资有什么区别', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_098', title: '减资需要登报吗', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_099', title: '公司变更需要多久', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_100', title: '公司变更要多少钱', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_101', title: '股权转让怎么操作', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },

    // —— 注销 ——
    { id: 'gs_102', title: '公司注销流程是什么', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_103', title: '公司注销需要什么资料', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_104', title: '公司注销要多久', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_105', title: '公司注销要多少钱', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_106', title: '简易注销和一般注销区别', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_107', title: '什么情况不能简易注销', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_108', title: '公司不注销会怎么样', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_109', title: '吊销和注销有什么区别', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_110', title: '公司吊销了还要注销吗', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_111', title: '个体户怎么注销', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_112', title: '个独怎么注销', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_113', title: '公司注销后还能恢复吗', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },
    { id: 'gs_114', title: '公司转让和注销哪个划算', tag: '工商', tagColor: 'gold', icon: FileTextOutlined },

    // —— 工商常识 ——
    { id: 'gs_115', title: '什么是统一社会信用代码', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },
    { id: 'gs_116', title: '什么是三证合一', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },
    { id: 'gs_117', title: '什么是五证合一', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },
    { id: 'gs_118', title: '什么是全程电子化登记', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },
    { id: 'gs_119', title: '什么是一照多址', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },
    { id: 'gs_120', title: '什么是集群注册', tag: '工商', tagColor: 'default', icon: QuestionCircleOutlined },

    // ================= 税务版块（80条） =================
    // —— 纳税人身份（新手最常问） ——
    { id: 'sw_001', title: '什么是小规模纳税人', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_002', title: '什么是一般纳税人', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_003', title: '小规模和一般纳税人区别', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_004', title: '新公司默认是什么纳税人', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_005', title: '小规模纳税人税率是多少', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_006', title: '一般纳税人税率是多少', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_007', title: '小规模可以开专票吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_008', title: '小规模怎么升级一般纳税人', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_009', title: '一般纳税人可以转小规模吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_010', title: '小规模年销售额超过500万怎么办', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_011', title: '一般纳税人可以开普票吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_012', title: '小规模纳税人免征增值税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 报税基础 ——
    { id: 'sw_013', title: '刚注册的公司要报税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_014', title: '零申报是什么意思', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_015', title: '不报税会怎么样', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_016', title: '公司多久报一次税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_017', title: '每个月几号前要报税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_018', title: '忘记报税怎么办', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_019', title: '逾期报税有什么后果', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_020', title: '税务异常怎么解除', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_021', title: '什么是税务登记', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_022', title: '什么是税种核定', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_023', title: '什么是发票核定', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_024', title: '发票额度怎么申请', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_025', title: '什么是金税盘和税控盘', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 发票 ——
    { id: 'sw_026', title: '普票和专票有什么区别', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_027', title: '专票可以抵扣吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_028', title: '普票可以抵扣吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_029', title: '发票丢了怎么办', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_030', title: '发票开错了怎么处理', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_031', title: '红冲发票是什么意思', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_032', title: '电子发票和纸质发票区别', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_033', title: '全电发票是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_034', title: '发票可以代开吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_035', title: '没有进项可以开销项吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 常见税种 ——
    { id: 'sw_036', title: '增值税是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_037', title: '企业所得税是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_038', title: '个人所得税是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_039', title: '附加税是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_040', title: '印花税是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_041', title: '企业所得税怎么算', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_042', title: '小微企业有什么税收优惠', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_043', title: '小规模月开票10万以内免税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_044', title: '个体户要交哪些税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_045', title: '个独要交哪些税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 个独 / 个体户税务（高频） ——
    { id: 'sw_046', title: '个独和个体户哪个更省税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_047', title: '个独需要交企业所得税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_048', title: '个体户需要交企业所得税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_049', title: '个独核定征收是什么意思', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_050', title: '个体户核定征收是什么意思', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_051', title: '查账征收和核定征收区别', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_052', title: '个独可以核定征收吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_053', title: '个体户可以核定征收吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_054', title: '核定征收政策还稳定吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_055', title: '个独注销要清税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 税务实务 ——
    { id: 'sw_056', title: '公司没业务可以不报税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_057', title: '公司不经营要报税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_058', title: '公司没收入怎么报税', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_059', title: '公司一直零申报可以吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_060', title: '长期零申报有什么风险', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_061', title: '公司注销前要清税吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_062', title: '清税证明怎么开', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_063', title: '税务注销流程是什么', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_064', title: '税务注销需要什么资料', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_065', title: '税务注销要多久', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_066', title: '公司变更法人要变更税务吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_067', title: '公司迁址要变更税务吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_068', title: '跨区迁移税务怎么处理', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_069', title: '什么是税务报到', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_070', title: '新公司税务报到要多久', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },

    // —— 社保 / 公积金 ——
    { id: 'sw_071', title: '公司必须给员工交社保吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_072', title: '公司可以只交一个人的社保吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_073', title: '个体户要交社保吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_074', title: '没有员工可以不开社保户吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_075', title: '社保开户需要什么资料', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_076', title: '公积金必须交吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_077', title: '社保和工资不一致可以吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_078', title: '法人需要在本公司交社保吗', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_079', title: '公司不交社保有什么风险', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
    { id: 'sw_080', title: '社保基数怎么确定', tag: '税务', tagColor: 'cyan', icon: QuestionCircleOutlined },
])

const filteredKnowledge = computed(() => {
    let list = knowledgeList.value
    if (kbActiveTag.value !== '全部') {
        list = list.filter(k => k.tag === kbActiveTag.value)
    }
    if (kbKeyword.value.trim()) {
        const kw = kbKeyword.value.trim().toLowerCase()
        list = list.filter(k => k.title.toLowerCase().includes(kw))
    }
    return list
})

// 搜索关键词高亮
const highlightText = (text: string) => {
    if (!kbKeyword.value.trim()) return text
    const kw = kbKeyword.value.trim()
    const reg = new RegExp(`(${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(reg, '<span class="kb-highlight">$1</span>')
}

// 点击知识卡片：填入输入框 + 自动选中上下文
const handleKnowledgeClick = (kb: KnowledgeItem) => {
    const idx = selectedKnowledge.value.indexOf(kb.id)
    if (idx === -1) {
        selectedKnowledge.value.push(kb.id)
    } else {
        selectedKnowledge.value.splice(idx, 1)
    }
    // 同时填入输入框，方便用户直接发送
    activeTab.value = 'chat'
    input.value = kb.title
    nextTick(() => {
        const el = document.querySelector('.chat-textarea textarea') as HTMLTextAreaElement
        el?.focus()
    })
}

// ========== 初始化 ==========
const cleanFailedMessages = () => {
    let changed = false
    conversations.value.forEach(conv => {
        const before = conv.messages.length
        conv.messages = conv.messages.filter(m =>
            !(m.role === 'assistant' && isFailedReply(m.content))
        )
        if (conv.messages.length !== before) changed = true
    })
    if (changed) saveConversations()
}

cleanFailedMessages()
ensureConversation()
loadAgents()

watch(open, (val) => {
    if (val) nextTick(scrollToBottom)
})
</script>

<style lang="less" scoped>
// ========== 主容器 ==========
.ai-float-wrap {
    position: fixed;
    bottom: 40px;
    right: 0px;
    z-index: 99998;
}

.ai-float-wrap.is-open {
    right: 40px;
}

// ========== 展开态面板 ==========
.wechat-panel {
    width: 1150px;
    max-width: 95vw;
    height: 720px;
    max-height: 88vh;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e0e0e0;
}

// ========== 顶部标题栏 ==========
.wechat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: #f7f7f7;
    border-bottom: 1px solid #e6e6e6;
    flex-shrink: 0;

    .header-left {
        display: flex;
        align-items: center;
        gap: 8px;

        .app-icon {
            font-size: 18px;
            color: #1677ff;
        }

        .app-title {
            font-size: 14px;
            font-weight: 600;
            color: #2c2c2c;
        }
    }

    .header-actions {
        display: flex;
        gap: 16px;

        .action-icon {
            font-size: 15px;
            color: #6b6b6b;
            cursor: pointer;
            transition: color 0.2s;

            &:hover {
                color: #1677ff;
            }
        }
    }
}

// ========== 主体三栏 ==========
.wechat-body {
    flex: 1;
    display: flex;
    overflow: hidden;
}

// ========== 左栏：会话列表 ==========
.conv-sidebar {
    width: 230px;
    background: #ededed;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    .conv-search {
        padding: 10px 12px;

        :deep(.ant-input-affix-wrapper) {
            background: #fff;
            border: none;
            border-radius: 4px;
            font-size: 12px;
        }
    }

    .conv-list {
        flex: 1;
        overflow-y: auto;
        padding: 0 8px 8px 8px;

        .conv-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.15s;
            margin-bottom: 2px;

            &:hover {
                background: #e0e0e0;
            }

            &.active {
                background: #d4d4d4;
            }

            .conv-avatar {
                width: 36px;
                height: 36px;
                border-radius: 6px;
                background: #1677ff;
                color: #fff;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 18px;
                flex-shrink: 0;
            }

            .conv-info {
                flex: 1;
                min-width: 0;

                .conv-title {
                    font-size: 13px;
                    font-weight: 500;
                    color: #2c2c2c;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .conv-preview {
                    font-size: 12px;
                    color: #999;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    margin-top: 2px;
                }
            }

            .conv-time {
                font-size: 11px;
                color: #b0b0b0;
                flex-shrink: 0;
                align-self: flex-start;
                margin-top: 2px;
            }
        }
    }
}

// ========== 中栏：聊天主区 ==========
.chat-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f5f5f5;
    min-width: 0;
}

.chat-tabs {
    display: flex;
    border-bottom: 1px solid #e6e6e6;
    background: #f7f7f7;
    flex-shrink: 0;

    .tab-item {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 10px 20px;
        font-size: 13px;
        color: #666;
        cursor: pointer;
        transition: all 0.2s;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;

        .anticon {
            font-size: 15px;
        }

        &:hover {
            color: #1677ff;
        }

        &.active {
            color: #1677ff;
            font-weight: 600;
            border-bottom-color: #1677ff;
            background: #fff;
        }
    }
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px 20px 10px 20px;

    .chat-welcome {
        text-align: center;
        padding: 60px 20px;

        .welcome-avatar {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background: #1677ff;
            color: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            margin-bottom: 16px;
        }

        .welcome-title {
            font-size: 15px;
            color: #2c2c2c;
            font-weight: 500;
            margin-bottom: 6px;
        }

        .welcome-sub {
            font-size: 13px;
            color: #999;
        }
    }

    .msg-row {
        display: flex;
        margin-bottom: 18px;
        gap: 10px;

        &.is-other {
            flex-direction: row;
        }

        &.is-me {
            flex-direction: row-reverse;
        }

        .msg-avatar {
            width: 38px;
            height: 38px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            flex-shrink: 0;
        }

        &.is-other .msg-avatar {
            background: #fff;
            border: 1px solid #e0e0e0;
            color: #1677ff;
        }

        &.is-me .msg-avatar {
            background: #1677ff;
            color: #fff;
        }

        .msg-body {
            max-width: 70%;
            display: flex;
            flex-direction: column;

            .msg-bubble {
                padding: 10px 14px;
                border-radius: 6px;
                font-size: 14px;
                line-height: 1.6;
                word-break: break-word;
                white-space: pre-wrap;
                overflow-wrap: anywhere;

                &.thinking {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #999;
                    font-size: 13px;
                }

                .msg-files {
                    margin-bottom: 6px;
                    padding-bottom: 6px;
                    border-bottom: 1px dashed rgba(0, 0, 0, 0.1);

                    .file-item {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        font-size: 12px;

                        .file-size {
                            opacity: 0.7;
                        }
                    }
                }

                .msg-images {
                    display: flex;
                    gap: 4px;
                    flex-wrap: wrap;
                    margin-bottom: 6px;

                    img {
                        max-width: 120px;
                        max-height: 120px;
                        border-radius: 4px;
                    }
                }

                :deep(.code-block) {
                    background: #1e1e1e;
                    color: #d4d4d4;
                    padding: 8px;
                    border-radius: 4px;
                    overflow-x: auto;
                    font-size: 12px;
                    display: block;
                    margin: 6px 0;
                }
            }

            .msg-actions {
                margin-top: 4px;
                display: flex;
                justify-content: flex-end;
                opacity: 0;
                transition: opacity 0.2s;

                .action-icon {
                    font-size: 13px;
                    color: #b0b0b0;
                    cursor: pointer;

                    &:hover {
                        color: #1677ff;
                    }
                }
            }

            &:hover .msg-actions {
                opacity: 1;
            }
        }

        &.is-me .msg-body {
            align-items: flex-end;

            .msg-bubble {
                background: #d6e4ff;
                color: #1f1f1f;
                border-top-right-radius: 2px;
            }
        }

        &.is-other .msg-body {
            align-items: flex-start;

            .msg-bubble {
                background: #fff;
                color: #2c2c2c;
                border: 1px solid #ebebeb;
                border-top-left-radius: 2px;
            }
        }
    }
}

.quick-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 16px;
    background: #f5f5f5;
    border-top: 1px solid #e9e9e9;
    flex-shrink: 0;

    .ant-tag {
        cursor: pointer;
        margin: 0;
        border-radius: 14px;
        padding: 2px 12px;
        font-size: 12px;
        background: #fff;
        border-color: #e0e0e0;
        color: #3c3c3c;
        transition: all 0.2s;

        &:hover {
            background: #e6f4ff;
            border-color: #1677ff;
            color: #1677ff;
        }
    }
}

.pending-files {
    display: flex;
    gap: 8px;
    padding: 8px 16px;
    background: #f5f5f5;
    flex-wrap: wrap;
    flex-shrink: 0;
    border-top: 1px solid #e9e9e9;

    .pending-item {
        position: relative;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px 4px 8px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 16px;
        font-size: 12px;
        color: #3c3c3c;

        img {
            width: 28px;
            height: 28px;
            border-radius: 4px;
            object-fit: cover;
        }

        .remove {
            color: #ff4d4f;
            cursor: pointer;
            font-size: 14px;
        }
    }
}

// 输入区：上输入框，下工具栏
.chat-input {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #e6e6e6;
    flex-shrink: 0;

    .chat-textarea {
        width: 100%;
        border: none;
        outline: none;
        resize: none;
        font-size: 14px;
        line-height: 1.6;
        padding: 0;
        background: transparent;
        box-shadow: none;

        :deep(.ant-input) {
            border: none !important;
            box-shadow: none !important;
            background: transparent;
            font-size: 14px;
            line-height: 1.6;
            padding: 0 !important;
            min-height: 72px;
        }

        :deep(textarea) {
            padding: 0 !important;
        }
    }

    .input-bottom-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .input-tools {
            display: flex;
            gap: 16px;

            .action-icon {
                font-size: 18px;
                color: #6b6b6b;
                cursor: pointer;
                transition: color 0.2s;

                &:hover {
                    color: #1677ff;
                }
            }
        }

        .send-btn {
            height: 36px;
            padding: 0 24px;
            border-radius: 6px;
            background: #1677ff;
            border-color: #1677ff;
            font-weight: 500;
            font-size: 14px;

            &:hover,
            &:focus {
                background: #0958d9;
                border-color: #0958d9;
            }

            &:disabled {
                background: #91caff;
                border-color: #91caff;
            }
        }
    }
}

// 数字员工
.ai-agents {
    flex: 1;
    padding: 16px;
    overflow-y: auto;

    .agents-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        font-size: 13px;
        font-weight: 600;
    }

    .agents-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;

        .agent-card {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
            background: #fff;

            &:hover {
                border-color: #1677ff;
                background: #f0f7ff;
            }

            &.active {
                border-color: #1677ff;
                background: #e6f4ff;
            }

            .agent-avatar {
                font-size: 26px;
            }

            .agent-info {
                .agent-name {
                    font-size: 13px;
                    font-weight: 500;
                }

                .agent-role {
                    font-size: 11px;
                    color: #999;
                }
            }
        }
    }

    .agent-chat-hint {
        margin-top: 14px;
        text-align: center;
        font-size: 12px;
        color: #666;
    }
}

// ========== 右栏：知识库 ==========
.knowledge-sidebar {
    width: 280px;
    background: #fafafa;
    border-left: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    .knowledge-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 14px 16px;
        background: #fff;
        border-bottom: 1px solid #e9e9e9;
        font-size: 14px;
        font-weight: 600;
        color: #2c2c2c;

        .kb-icon {
            color: #1677ff;
            font-size: 18px;
        }

        :deep(.ant-badge) {
            margin-left: auto;
        }
    }

    .knowledge-search {
        padding: 10px 12px 6px;

        :deep(.ant-input-affix-wrapper) {
            background: #fff;
            border: 1px solid #e6e6e6;
            border-radius: 4px;
            font-size: 12px;
        }
    }

    .kb-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 0 12px 8px;

        .kb-tab {
            font-size: 11px;
            padding: 2px 10px;
            border-radius: 10px;
            background: #f0f0f0;
            color: #666;
            cursor: pointer;
            transition: all 0.15s;

            &:hover {
                background: #e6f4ff;
                color: #1677ff;
            }

            &.active {
                background: #1677ff;
                color: #fff;
            }
        }
    }

    .knowledge-list {
        flex: 1;
        overflow-y: auto;
        padding: 0 10px 10px 10px;
        display: flex;
        flex-direction: column;
        gap: 6px;

        .knowledge-card {
            background: #fff;
            border-radius: 6px;
            padding: 8px 10px;
            border: 1px solid #f0f0f0;
            transition: all 0.15s;
            cursor: pointer;

            &:hover {
                border-color: #1677ff;
                background: #f0f7ff;
            }

            &.active {
                border-color: #1677ff;
                background: #f0f7ff;
            }

            .kb-card-head {
                display: flex;
                align-items: center;
                gap: 6px;

                .kb-card-icon {
                    color: #1677ff;
                    font-size: 14px;
                    flex-shrink: 0;
                }

                .kb-card-title {
                    font-size: 12.5px;
                    font-weight: 500;
                    color: #1f1f1f;
                    flex: 1;
                    line-height: 1.4;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .kb-checked {
                    color: #1677ff;
                    font-size: 14px;
                    flex-shrink: 0;
                }
            }
        }

        :deep(.kb-highlight) {
            background: #fff7cc;
            color: #d48806;
            padding: 0 2px;
            border-radius: 2px;
        }
    }

    .knowledge-tip {
        padding: 12px 16px;
        background: #f0f7ff;
        border-top: 1px solid #bae0ff;
        font-size: 12px;
        color: #1677ff;
        display: flex;
        align-items: center;
        gap: 8px;

        .anticon {
            font-size: 16px;
        }
    }
}

// ========== 收起态：右侧贴边小机器人（Ant Design 蓝） ==========
.ai-dock {
    position: relative;
    width: 64px;
    height: 120px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;

    transform: translateX(48px);
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform;

    &:hover {
        transform: translateX(0);
    }

    .dock-robot {
        position: relative;
        width: 56px;
        height: 68px;
        animation: dockBob 2.6s ease-in-out infinite;
    }

    .robot-antenna {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 3px;
        height: 10px;
        background: #1677ff;
        border-radius: 2px;

        .antenna-ball {
            position: absolute;
            top: -5px;
            left: 50%;
            transform: translateX(-50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #1677ff;
            box-shadow: 0 0 8px rgba(22, 119, 255, 0.8);
            animation: antennaBlink 1.8s ease-in-out infinite;
        }
    }

    .robot-head {
        position: absolute;
        top: 10px;
        left: 0;
        width: 56px;
        height: 42px;
        background: linear-gradient(135deg, #4096ff, #1677ff);
        border-radius: 14px 14px 16px 16px;
        box-shadow: 0 4px 16px rgba(22, 119, 255, 0.45),
            inset 0 -4px 0 rgba(0, 0, 0, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        .robot-eye {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #fff;
            position: relative;
            animation: eyeBlink 3.2s infinite;

            &::after {
                content: '';
                position: absolute;
                top: 2px;
                left: 2px;
                width: 5px;
                height: 5px;
                border-radius: 50%;
                background: #0958d9;
            }
        }

        .robot-mouth {
            position: absolute;
            bottom: 8px;
            left: 50%;
            transform: translateX(-50%);
            width: 14px;
            height: 3px;
            background: rgba(255, 255, 255, 0.9);
            border-radius: 2px;
        }
    }

    .robot-body {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 18px;
        background: linear-gradient(135deg, #4096ff, #1677ff);
        border-radius: 8px 8px 10px 10px;
        box-shadow: 0 3px 10px rgba(22, 119, 255, 0.35),
            inset 0 -3px 0 rgba(0, 0, 0, 0.08);

        .robot-arm {
            position: absolute;
            top: 4px;
            width: 6px;
            height: 10px;
            background: #1677ff;
            border-radius: 4px;

            &.left {
                left: -6px;
                transform: rotate(12deg);
            }

            &.right {
                right: -6px;
                transform: rotate(-12deg);
            }
        }
    }

    .dock-label {
        margin-top: 6px;
        font-size: 11px;
        color: #fff;
        background: linear-gradient(135deg, #4096ff, #1677ff);
        padding: 3px 6px;
        border-radius: 4px;
        white-space: nowrap;
        writing-mode: vertical-rl;
        letter-spacing: 1px;
        box-shadow: -2px 2px 10px rgba(22, 119, 255, 0.3);
    }
}

@keyframes dockBob {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-4px);
    }
}

@keyframes antennaBlink {

    0%,
    100% {
        opacity: 1;
        transform: translateX(-50%) scale(1);
    }

    50% {
        opacity: 0.6;
        transform: translateX(-50%) scale(1.3);
    }
}

@keyframes eyeBlink {

    0%,
    92%,
    100% {
        transform: scaleY(1);
    }

    95% {
        transform: scaleY(0.1);
    }
}
</style>
